import { Stack, Group } from '@mantine/core'
import KPI_Section from 'app/views/components/KPI/KPI-horizontal-section'
import MainTable from 'app/views/components/Table/MainTable'
import BulkActionsButton from 'app/views/components/buttons/bulk-actions-button'
import getColumnDefs from './facility-details/facility-table-columnDefs'
import { useEffect, useMemo, useState } from 'react'
import { FacilityParams } from 'app/models/facility-params'
import { facilityScreenRowsAsParams } from 'app/models/facility-screen-row'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getKPIData } from './facilities-kpi-data'
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification'
import { approveBulkFacilities, loadAllFacilities, submitBulkFacilities } from 'app/state/ducks/facilities/thunks'
import { loadFacilityScreen } from 'app/state/ducks/facility-screen/thunks'
import { setFacilityScreenStatuses } from 'app/state/ducks/facility-screen/actions'
import {
  getFacilityScreenLoader,
  getFacilityScreenRows,
  isFacilityScreenStale,
} from 'app/state/ducks/facility-screen/selectors'
import { GridSortingStorage, GridFilterStorage, GridPaginationStorage, GridColumnVisibilityStorage, useGridSorting, useGridFilters, useGridPagination, useGridColumnVisibility } from 'app/utils/grid-sorting-storage'

/**
 * The Facilities grid, from ONE request: GET /api/facility-management/screen
 * returns the rows the table renders — deal and admin names, holds, the
 * non-pro-rata flag and whether lenders hold positions already resolved, an
 * amendment in flight shown in place of the facility it amends. The store
 * follows the commands (the facilities and facility-amendments actions), so
 * nothing is refetched after a write the rows can be patched for.
 *
 * The row menu's forms fetch the one facility they act on, when they open.
 */
export default function FacilitiesContentPage() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const rows = useSelector(getFacilityScreenRows)
  const screenLoading = useSelector(getFacilityScreenLoader)
  const stale = useSelector(isFacilityScreenStale)
  const [bulkRunning, setBulkRunning] = useState(false)
  const globalLoading = screenLoading || bulkRunning
  // the Terminated grid has its own page; this one shows the live facilities
  const facilities = useMemo(
    () => facilityScreenRowsAsParams(rows.filter(row => row.status !== 'Terminated')),
    [rows],
  )
  const [filteredFacilities, setFilteredFacilities] = useState<FacilityParams[]>(facilities)
  const [selectedBulkFacilities, setSelectedBulkFacilities] = useState<FacilityParams[]>([])
  const [tableSelectedRows, setTableSelectedRows] = useState<Record<string, boolean>>({});

  // Grid sorting with localStorage persistence
  const { sorting, handleSortingChange } = useGridSorting(GridSortingStorage.facilities)

  // Grid filters with localStorage persistence
  const { toMRTColumnFilters, handleMRTFiltersChange } = useGridFilters(GridFilterStorage.facilities)

  // Grid pagination with localStorage persistence
  const { pagination, handlePaginationChange } = useGridPagination(GridPaginationStorage.facilities)

  // Grid column visibility with localStorage persistence
  const { columnVisibility, handleColumnVisibilityChange } = useGridColumnVisibility(
    GridColumnVisibilityStorage.facilities,
    ['systemId'] // Always hide systemId
  )

  const kpiData = getKPIData(facilities, setFilteredFacilities)

  useEffect(() => {
    dispatch(loadFacilityScreen())
  }, [])

  // A write the rows could not be patched for: reload once, while on screen.
  // Also runs when a load ends, for an invalidation that arrived during it.
  useEffect(() => {
    if (stale && !screenLoading) dispatch(loadFacilityScreen())
  }, [stale, screenLoading])

  useEffect(() => {
    setFilteredFacilities(facilities)
  }, [facilities])

  /**
   * The bulk endpoints address a facility by its id and its key admin, both on
   * the row. They answer per facility; the rows the ledger accepted flip in
   * place, rather than being refetched while PQS may still be projecting them.
   */
  const runBulk = async (
    action: (facilities: FacilityParams[]) => any,
    status: 'Submitted' | 'Approved',
    title: string,
  ) => {
    setBulkRunning(true)
    try {
      const result: any = await dispatch(action(selectedBulkFacilities) as any)
      if (!result?.success) {
        ErrorNotification({ title: `${title} Failed`, message: result?.payload || `${title} failed` })
        return
      }
      const accepted = (Array.isArray(result.payload) ? result.payload : [])
        .filter((response: any) => response?.id && !response.ErrorMessage && !response.error)
        .map((response: any) => response.id as string)
      dispatch(setFacilityScreenStatuses({ ids: accepted, status }))
      SuccessNotification({
        title: `Successfully ${status} Bulk Facilities`,
        message: 'This process does allow partial successes, double check for failures',
      })
      // The facility store the details pages read follows, as it always did —
      // not awaited: the rows are already flipped, and the grid's store fills
      // the approved rows' system ids from it when it lands.
      dispatch(loadAllFacilities())
    } finally {
      setBulkRunning(false)
    }
  }

  const bulkFacilityActions = {
    'Submit Facilities': () => runBulk(submitBulkFacilities, 'Submitted', 'Bulk Submit'),
    'Approve Facilities': () => runBulk(approveBulkFacilities, 'Approved', 'Bulk Approve'),
  }

  const handleRowSelection = (selectedRows: FacilityParams[]) => {
    setSelectedBulkFacilities(selectedRows)
  };

  return (
    <Stack>
      <KPI_Section
        kpiData={kpiData}
        isLoading={globalLoading}
      />
      <Stack className='optionalTableHeader bulkActions'>
        <Group w="100%">
          <BulkActionsButton selectedItems={selectedBulkFacilities} clearSelectedItems={() => {
            setSelectedBulkFacilities([]);
            setTableSelectedRows({})
          }} actions={bulkFacilityActions} />
        </Group>
      </Stack>
      <MainTable
        tableName='Facilities Table'
        isLoading={globalLoading}
        withPagination={true}
        csvExportEnabled={true}
        enableTopToolbar={false}
        headerBackgroundColor='#F0EEEE'
        columnDefs={getColumnDefs()}
        data={filteredFacilities}
        enableRowSelection={true}
        enableMultiRowSelection={true}
        hiddenColumns={['systemId']} //this is only used in export and not shown in the table
        tableSelectedRows={tableSelectedRows}
        setTableSelectedRows={setTableSelectedRows}
        initialSorting={[{ id: sorting.sortBy, desc: sorting.order === 'desc' }]}
        onSortingChange={handleSortingChange}
        initialColumnFilters={toMRTColumnFilters()}
        onColumnFiltersChange={handleMRTFiltersChange}
        initialPagination={{ pageSize: pagination.pageSize, pageIndex: pagination.pageIndex }}
        onPaginationChange={handlePaginationChange}
        initialColumnVisibility={columnVisibility}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        setRow={(e: FacilityParams, cellName: string) => {
          if (cellName != 'id') {
            navigate('/dealmanagement/facilities/' + e.id + '/' + e.status)
          }
        }}
        selectedRows={handleRowSelection}
      />
    </Stack>
  )
}
